import { NavLink } from "react-router-dom";
import * as Icons from "lucide-react";

// Tabs visible only to unrestricted Finance users (admin/partner/billing).
// Scoped users (finance_team_scope set) are restricted to invoices and payment reports only.
export const FIN_TABS_ALL = [
  ["/finance", "Dashboard", "LayoutDashboard", true],
  ["/finance/invoices", "Invoices", "FileText"],
  ["/finance/payments", "Payments", "Banknote"],
  ["/finance/receivables", "Receivables", "AlarmClock"],
  ["/finance/pipeline", "Revenue Pipeline", "TrendingUp"],
  ["/finance/payment-reports", "Payment Reports", "ClipboardList"],
  ["/finance/payment-methods", "Pay Methods", "SlidersHorizontal"],
];

// Gate C: tabs available to users with a scoped individual Finance grant
// (finance_team_scope is non-empty — engagement-restricted access).
export const FIN_TABS_SCOPED = [
  ["/finance/invoices", "Invoices", "FileText"],
  ["/finance/payment-reports", "Payment Reports", "ClipboardList"],
];

// Kept for callers that do not need scope-aware filtering.
export const FIN_TABS = FIN_TABS_ALL;

/**
 * Returns true when the user has an individual (scoped) Finance grant.
 * Scoped users have a non-empty finance_team_scope and do NOT hold an
 * unrestricted Finance role (administrator / partner / billing).
 */
export function isScopedFinanceUser(user) {
  if (!user) return false;
  const scope = user.finance_team_scope || [];
  if (!scope.length) return false;
  // Unrestricted roles — their modules come from role, not individual grants
  if (["administrator", "partner", "billing"].includes(user.role)) return false;
  return true;
}

export function money(v, ccy = "USD") {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${ccy} ${n.toFixed(2)}`;
  }
}

export const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-600", pending_approval: "bg-amber-100 text-amber-700",
  issued: "bg-blue-100 text-blue-700", sent: "bg-blue-100 text-blue-700", viewed: "bg-indigo-100 text-indigo-700",
  partially_paid: "bg-cyan-100 text-cyan-700", paid: "bg-emerald-100 text-emerald-700",
  payment_submitted: "bg-violet-100 text-violet-700",
  overpayment: "bg-orange-100 text-orange-700",
  overdue: "bg-rose-100 text-rose-700", refunded: "bg-purple-100 text-purple-700",
  written_off: "bg-slate-200 text-slate-500", void: "bg-slate-200 text-slate-400",
};

/** Gate C: notice displayed to scoped Finance users to clarify the limited list scope. */
export function ScopedLedgerNotice({ teamScope }) {
  if (!teamScope || !teamScope.length) return null;
  return (
    <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="scoped-ledger-notice">
      <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
      <span>
        <strong>Scoped view — not a complete accounting ledger.</strong>{" "}
        You are viewing invoices linked to engagements assigned to your authorized team
        ({teamScope.join(", ")}). Invoices without a direct engagement link are not shown.
        Contact a Finance administrator for firm-wide access.
      </span>
    </div>
  );
}

export function FinanceTabs({ user }) {
  const scoped = isScopedFinanceUser(user);
  const tabs = scoped ? FIN_TABS_SCOPED : FIN_TABS_ALL;
  return (
    <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="finance-tabs">
      {tabs.map(([to, label, icon, end]) => {
        const Icon = Icons[icon];
        return (
          <NavLink key={to} to={to} end={end} data-testid={`finance-tab-${label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                isActive ? "border-[#F5A623] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {Icon && <Icon className="h-4 w-4" />}{label}
          </NavLink>
        );
      })}
    </div>
  );
}

export function FinanceHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-3">
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wide">Finance &amp; Revenue</p>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

